import { defineStore } from "pinia";
import { inject, ref, computed, readonly } from "vue";
import { PAGE_APP_SYMBOL, EMBED_APP_SYMBOL } from "_Main/injectionSymbols";
import { getDateFromDotnetTicks } from "Util/clientDate.js";
import {
    PROJECT_FORM_CONTROLLER_NAMESPACE,
    PROJECT_FORM_CONTROLLER_TYPES
} from "Forms/Components/ProjectForm/projectFormControllerStore";
import { isModuleRegistered } from "SharedStores/util";
import { useStore } from "vuex";

export const useProjectLockStore = defineStore("projectLockStore", () => {
    const app = inject(PAGE_APP_SYMBOL, null) ?? inject(EMBED_APP_SYMBOL);
    const store = useStore();

    const lockedForPendingPayment = ref(false);
    const lockedForProjectTable = ref(false);
    const projectTableOperationProgress = ref(null);
    const _projectTableOperationId = ref(null);
    const _projectTableOperationPromise = ref(null);

    const isLocked = computed(() => lockedForPendingPayment.value || lockedForProjectTable.value);

    const canCancelProjectTableImport = computed(() => {
        if (
            !projectTableOperationProgress.value ||
            !isModuleRegistered(PROJECT_FORM_CONTROLLER_NAMESPACE, store.state)
        ) {
            return false;
        }

        const logs = projectTableOperationProgress.value.Logs;
        if (!logs) {
            return false;
        }

        // Show the cancel button if there has been no activity for at least one minute
        const lastLogDate = getDateFromDotnetTicks(logs[0].LogDate.Ticks);
        return lastLogDate < Date.now() - 60000;
    });

    function loadState(lockState) {
        if (lockState) {
            lockedForPendingPayment.value = lockState.LockedForPendingPayment;
            lockedForProjectTable.value = lockState.LockedForProjectTable;

            if (lockState.ProjectTableOperationId) {
                _projectTableOperationId.value = lockState.ProjectTableOperationId;
                _beginProjectTableOperation();
            }
        }
    }

    async function cancelProjectTableImport() {
        if (!canCancelProjectTableImport.value) return;
        const importResult = await store.dispatch(
            PROJECT_FORM_CONTROLLER_TYPES.actions.cancelProjectTableImport,
            { operationId: _projectTableOperationId.value },
            { root: true }
        );
        if (importResult.status === "success") {
            _projectTableOperationId.value = null;
            _projectTableOperationPromise.value = null;
            projectTableOperationProgress.value = null;
            lockedForProjectTable.value = false;
        }
    }

    function waitForProjectTableOperation(operationId) {
        if (!operationId) return;

        // Replace the current operation if none is running
        if (!_projectTableOperationPromise.value) {
            _projectTableOperationId.value = operationId;
            _beginProjectTableOperation();
        }
        return _projectTableOperationPromise.value;
    }

    function _beginProjectTableOperation() {
        // Begin a new operation if none is running
        if (!_projectTableOperationPromise.value) {
            lockedForProjectTable.value = true;
            projectTableOperationProgress.value = null;
            _projectTableOperationPromise.value = app
                .waitForLongOperation({
                    operationId: _projectTableOperationId.value,
                    pollIntervalSeconds: 2,
                    progress: (result) => {
                        if (result.status === "success") {
                            projectTableOperationProgress.value = result.Data;
                        }
                    }
                })
                .then((result) => {
                    if (result.Data.IsFailed) {
                        app.showErrorDialog(null, result.Data.Output);
                    }
                    _projectTableOperationPromise.value = null;
                    lockedForProjectTable.value = false;
                });
        }
    }

    function setLockedForPendingPayment(isLocked) {
        lockedForPendingPayment.value = isLocked;
    }

    return {
        lockedForPendingPayment: readonly(lockedForPendingPayment),
        lockedForProjectTable: readonly(lockedForProjectTable),
        projectTableOperationProgress: readonly(projectTableOperationProgress),
        isLocked,
        canCancelProjectTableImport,
        loadState,
        cancelProjectTableImport,
        waitForProjectTableOperation,
        setLockedForPendingPayment
    };
});
